/* eslint-disable no-param-reassign */

export type OutputType = 'base64' | 'blob' | 'file';

interface ResizeImageOptions {
  file: File;
  maxWidth: number;
  maxHeight: number;
  compressFormat: 'jpeg' | 'png' | 'webp';
  quality: number;
  rotation: number;
  keepAspectRatio?: boolean;
  responseUriFunc: (data: string | Blob | File) => void;
  outputType?: OutputType;
  minWidth?: number | null;
  minHeight?: number | null;
}

class Resizer {
  static changeHeightWidth(
    height: number,
    maxHeight: number,
    width: number,
    maxWidth: number,
    minWidth?: number | null,
    minHeight?: number | null,
    keepAspectRatio?: boolean,
  ): { height: number; width: number } {
    if (keepAspectRatio) {
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }
      if (minWidth && width < minWidth) {
        height = Math.round((height * minWidth) / width);
        width = minWidth;
      }
      if (minHeight && height < minHeight) {
        width = Math.round((width * minHeight) / height);
        height = minHeight;
      }
    } else {
      if (width > maxWidth) width = maxWidth;
      if (height > maxHeight) height = maxHeight;
      if (minWidth && width < minWidth) width = minWidth;
      if (minHeight && height < minHeight) height = minHeight;
    }

    return { height, width };
  }

  static resizeAndRotateImage(
    image: HTMLImageElement,
    maxWidth: number,
    maxHeight: number,
    minWidth: number | null,
    minHeight: number | null,
    compressFormat: 'jpeg' | 'png' | 'webp' = 'jpeg',
    quality = 100,
    rotation = 0,
    keepAspectRatio = true,
  ): string {
    const qualityDecimal = quality / 100;
    const canvas = document.createElement('canvas');

    let { width, height } = image;

    const newHeightWidth = this.changeHeightWidth(
      height,
      maxHeight,
      width,
      maxWidth,
      minWidth,
      minHeight,
      keepAspectRatio,
    );

    // define as dimensões do canvas com base no resultado proporcional
    if (rotation && (rotation === 90 || rotation === 270)) {
      canvas.width = newHeightWidth.height;
      canvas.height = newHeightWidth.width;
    } else {
      canvas.width = newHeightWidth.width;
      canvas.height = newHeightWidth.height;
    }

    width = newHeightWidth.width;
    height = newHeightWidth.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    ctx.fillStyle = 'rgba(0, 0, 0, 0)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (ctx.imageSmoothingEnabled && ctx.imageSmoothingQuality) {
      ctx.imageSmoothingQuality = 'high';
    }

    if (rotation) {
      ctx.rotate((rotation * Math.PI) / 180);
      if (rotation === 90) {
        ctx.translate(0, -canvas.width);
      } else if (rotation === 180) {
        ctx.translate(-canvas.width, -canvas.height);
      } else if (rotation === 270) {
        ctx.translate(-canvas.height, 0);
      }
    }

    ctx.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL(`image/${compressFormat}`, qualityDecimal);
  }


  static b64toByteArrays(b64Data: string): Uint8Array[] {
    const sliceSize = 512;
    const byteCharacters = atob(b64Data.replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, ''));
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return byteArrays;
  }

  static b64toBlob(b64Data: string, contentType: string): Blob {
    const byteArrays = this.b64toByteArrays(b64Data);
    return new Blob(byteArrays, { type: contentType });
  }

  static b64toFile(b64Data: string, fileName: string, contentType: string): File {
    const byteArrays = this.b64toByteArrays(b64Data);
    return new File(byteArrays, fileName, { type: contentType });
  }

  static createResizedImage(
    file: File,
    maxWidth: number,
    maxHeight: number,
    compressFormat: 'jpeg' | 'png' | 'webp',
    quality: number,
    rotation: number,
    responseUriFunc: (data: string | Blob | File) => void,
    outputType: OutputType = 'base64',
    minWidth: number | null = null,
    minHeight: number | null = null,
    keepAspectRatio = true,
  ) {
    const reader = new FileReader();

    if (!file || (file.type && !file.type.includes('image'))) {
      throw new Error('File Is NOT Image!');
    }

    reader.readAsDataURL(file);
    reader.onload = () => {
      const image = new Image();
      image.src = reader.result as string;
      image.onload = function () {
        const resizedDataUrl = Resizer.resizeAndRotateImage(
          image,
          maxWidth,
          maxHeight,
          minWidth,
          minHeight,
          compressFormat,
          quality,
          rotation,
          keepAspectRatio,
        );

        const contentType = `image/${compressFormat}`;
        switch (outputType) {
          case 'blob': {
            const blob = Resizer.b64toBlob(resizedDataUrl, contentType);
            responseUriFunc(blob);
            break;
          }
          case 'base64': {
            responseUriFunc(resizedDataUrl);
            break;
          }
          case 'file': {
            const fileNameWithoutFormat = file.name.replace(/\.(png|jpeg|jpg|webp)$/i, '');
            const newFileName = `${fileNameWithoutFormat}.${compressFormat}`;
            const newFile = Resizer.b64toFile(resizedDataUrl, newFileName, contentType);
            responseUriFunc(newFile);
            break;
          }
          default:
            responseUriFunc(resizedDataUrl);
        }
      };
    };

    reader.onerror = (error) => {
      throw new Error(error.toString());
    };
  }
}

export default {
  imageFileResizer: ({
    file,
    maxWidth,
    maxHeight,
    compressFormat,
    quality,
    rotation,
    keepAspectRatio = true,
    responseUriFunc,
    outputType = 'base64',
    minWidth = null,
    minHeight = null,
  }: ResizeImageOptions) =>
    Resizer.createResizedImage(
      file,
      maxWidth,
      maxHeight,
      compressFormat,
      quality,
      rotation,
      responseUriFunc,
      outputType,
      minWidth,
      minHeight,
      keepAspectRatio,
    ),
};
